'use client'

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Trees } from 'lucide-react'
import { WagamourV2Modal } from '@/components/v2/WagamourV2Modal'
import { WagamourV2Button } from '@/components/v2/WagamourV2Button'
import {
  WagamourV2ModalChipToggle,
  WagamourV2ModalEyebrow,
  WagamourV2ModalStoryLine,
} from '@/components/v2/WagamourV2ModalContent'
import { PlayCircleOrbitAvatar } from '@/components/play/PlayCircleOrbitAvatar'
import type { PlayCircleOrbitFriend } from '@/lib/play/playCircleOrbitMapper'
import {
  createPlaydateBuilderDraft,
  formatPlaydateBuilderWhenSummary,
  getPlaydateDateLabel,
  getPlaydateTimeLabel,
  PLAYDATE_EXTENDED_DATE_FIXTURES,
  PLAYDATE_PRIMARY_DATE_FIXTURES,
  PLAYDATE_TIME_WINDOWS,
  type PlaydateBuilderDraft,
  type PlaydateTimeWindow,
} from '@/lib/dev-preview-play/playdateBuilderFixtures'
import { buildCirclePlaydateScheduleFromDraft } from '@/lib/play/circlePlaydateScheduleFromBuilder'
import {
  buildPlaydateParkOptions,
  handoffSeedToParkOption,
  resolveBuilderStartStep,
  resolvePlaydateParkOption,
  type NearbyPlaydateParkInput,
  type PlayParkHandoffSeed,
} from '@/lib/play/playParkHandoff'
import { trackPlayV2Event } from '@/lib/play/playV2Analytics'
import {
  v2ModalInputClassName,
  v2ModalSelectStyle,
  v2ModalStyles,
} from '@/lib/design/v2ModalContent'
import { playV2FocusRing } from '@/lib/play/playV2Focus'
import { cn } from '@/lib/utils'

type WizardStep = 'dogs' | 'park' | 'date' | 'time' | 'review' | 'success'

const WIZARD_STEPS: WizardStep[] = ['dogs', 'park', 'date', 'time', 'review']

export type PlaydateBuilderPreviewSheetProps = {
  isOpen: boolean
  onClose: () => void
  activeDog: { name: string; photoUrl?: string | null }
  seedFriend: PlayCircleOrbitFriend
  portalContainer?: HTMLElement | null
  returnFocusRef?: React.RefObject<HTMLElement | null>
  /** Parks → Play handoff seed (preselect park once). */
  seedPark?: PlayParkHandoffSeed | null
  /** Fired once when seed park is applied into the draft. */
  onSeedParkApplied?: () => void
  /**
   * Parks near the app’s canonical selected location (Home / Parks / Picks).
   * Production must pass these — never rely on SF fixture parks.
   */
  nearbyParks?: readonly NearbyPlaydateParkInput[] | null
  /** True while canonical location or nearby search is still hydrating. */
  nearbyParksLoading?: boolean
  /**
   * Preview/dev only. Allows SF fixture parks when nearby is empty.
   * Keep false in production app-beta.
   */
  allowFixtureParks?: boolean
  /** When set, review submits a persistent proposal instead of preview-only success. */
  persistence?: {
    dogId: string
    relationshipId: string
    onProposed?: (playdateId: string) => void
  } | null
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string; code?: string }
  | { kind: 'success'; playdateId: string }

function buildInitialDraft(
  friendId: string,
  seedPark: PlayParkHandoffSeed | null | undefined
): PlaydateBuilderDraft {
  const draft = createPlaydateBuilderDraft(friendId)
  if (!seedPark) return draft
  const option = handoffSeedToParkOption(seedPark)
  return { ...draft, parkId: option.id }
}

export function PlaydateBuilderPreviewSheet({
  isOpen,
  onClose,
  activeDog,
  seedFriend,
  portalContainer = null,
  returnFocusRef,
  seedPark = null,
  onSeedParkApplied,
  nearbyParks = null,
  nearbyParksLoading = false,
  allowFixtureParks = false,
  persistence = null,
}: PlaydateBuilderPreviewSheetProps) {
  const titleId = useId()
  const [step, setStep] = useState<WizardStep>('dogs')
  const [draft, setDraft] = useState<PlaydateBuilderDraft>(() =>
    buildInitialDraft(seedFriend.id, seedPark)
  )
  const [showExtendedDates, setShowExtendedDates] = useState(false)
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' })
  const [stripCanonicalParkId, setStripCanonicalParkId] = useState(false)
  const seedAppliedRef = useRef(false)
  const reviewViewedRef = useRef(false)
  const planningStartedRef = useRef(false)

  const persistEnabled = Boolean(persistence?.relationshipId && persistence?.dogId)
  const parkOptions = useMemo(
    () =>
      buildPlaydateParkOptions(seedPark, {
        nearbyParks,
        allowFixtures: allowFixtureParks,
      }),
    [seedPark, nearbyParks, allowFixtureParks]
  )

  const reset = useCallback(() => {
    const hasPark = Boolean(seedPark)
    setStep(resolveBuilderStartStep(hasPark))
    setDraft(buildInitialDraft(seedFriend.id, seedPark))
    setShowExtendedDates(false)
    setSubmitState({ kind: 'idle' })
    setStripCanonicalParkId(false)
    reviewViewedRef.current = false
    planningStartedRef.current = false
    seedAppliedRef.current = false
  }, [seedFriend.id, seedPark])

  const handleClose = useCallback(() => {
    reset()
    onClose()
    window.requestAnimationFrame(() => {
      returnFocusRef?.current?.focus()
    })
  }, [onClose, reset, returnFocusRef])

  useEffect(() => {
    if (!isOpen) return
    reset()
    if (!planningStartedRef.current) {
      planningStartedRef.current = true
      trackPlayV2Event(
        'playdate_planning_started',
        {
          activeDogId: persistence?.dogId ?? null,
          relationshipId: persistence?.relationshipId ?? null,
          parkId: seedPark?.parkId ?? null,
          source: seedPark ? 'parks' : 'builder',
        },
        {
          dedupeKey: `playdate_planning_started_${persistence?.relationshipId ?? seedFriend.id}_${seedPark?.consumeKey ?? 'none'}`,
        }
      )
    }
    if (seedPark && !seedAppliedRef.current) {
      seedAppliedRef.current = true
      onSeedParkApplied?.()
      if (seedPark.parkId) {
        trackPlayV2Event(
          'playdate_park_selected',
          {
            parkId: seedPark.parkId,
            source: 'parks',
            relationshipId: persistence?.relationshipId ?? null,
            activeDogId: persistence?.dogId ?? null,
          },
          { dedupeKey: `playdate_park_selected_handoff_${seedPark.consumeKey}` }
        )
      }
    }
  }, [isOpen, reset, seedPark, onSeedParkApplied, persistence, seedFriend.id])

  useEffect(() => {
    if (!isOpen || step !== 'review' || reviewViewedRef.current) return
    reviewViewedRef.current = true
    trackPlayV2Event(
      'playdate_review_viewed',
      {
        activeDogId: persistence?.dogId ?? null,
        relationshipId: persistence?.relationshipId ?? null,
        parkId: seedPark?.parkId ?? draft.parkId,
        schedulingMode: draft.timeMode,
        source: 'builder',
      },
      {
        dedupeKey: `playdate_review_viewed_${persistence?.relationshipId ?? seedFriend.id}_${draft.dateId}_${draft.timeMode}_${draft.timeWindow ?? draft.exactTime}`,
      }
    )
  }, [
    isOpen,
    step,
    draft.dateId,
    draft.timeMode,
    draft.timeWindow,
    draft.exactTime,
    draft.parkId,
    persistence,
    seedFriend.id,
    seedPark?.parkId,
  ])

  const activeName = activeDog.name.trim() || 'Your dog'
  const friendName = seedFriend.name.trim() || 'Friend'

  const stepTitle = useMemo(() => {
    switch (step) {
      case 'dogs':
        return 'Who is coming'
      case 'park':
        return 'Choose a park'
      case 'date':
        return 'Pick a day'
      case 'time':
        return 'Pick a time'
      case 'review':
        return 'Review your plan'
      case 'success':
        return persistEnabled ? 'Invitation sent' : 'Invitation preview'
      default:
        return 'Plan a playdate'
    }
  }, [step, persistEnabled])

  const canContinue = useMemo(() => {
    switch (step) {
      case 'dogs':
        return true
      case 'park':
        return Boolean(draft.parkId)
      case 'date':
        return Boolean(draft.dateId)
      case 'time':
        return draft.timeMode === 'exact'
          ? Boolean(draft.exactTime && /^\d{2}:\d{2}$/.test(draft.exactTime))
          : Boolean(draft.timeWindow)
      case 'review':
        return submitState.kind !== 'submitting'
      default:
        return false
    }
  }, [draft, step, submitState.kind])

  const goBack = () => {
    const idx = WIZARD_STEPS.indexOf(step)
    if (idx > 0) setStep(WIZARD_STEPS[idx - 1]!)
  }

  const submitProposal = useCallback(
    async () => {
      if (!persistence) return
      const strip = stripCanonicalParkId
      const schedule = buildCirclePlaydateScheduleFromDraft(draft, {
        parkOptions,
        stripCanonicalParkId: strip,
      })
      if (!schedule) {
        setSubmitState({
          kind: 'error',
          message: 'Choose a park, day, and time to continue.',
          code: 'SCHEDULE_INCOMPLETE',
        })
        return
      }

      setSubmitState({ kind: 'submitting' })
      trackPlayV2Event('playdate_proposal_submitted', {
        activeDogId: persistence.dogId,
        relationshipId: persistence.relationshipId,
        parkId: schedule.parkId ?? null,
        schedulingMode: draft.timeMode,
        source: 'builder',
      })

      try {
        const res = await fetch('/api/play/playdates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': `builder-${persistence.relationshipId}-${schedule.scheduledStartAt}`,
          },
          body: JSON.stringify({
            dogId: persistence.dogId,
            relationshipId: persistence.relationshipId,
            ...schedule,
          }),
        })
        const json = (await res.json().catch(() => ({}))) as {
          success?: boolean
          code?: string
          error?: string
          playdate?: { id: string }
        }
        if (!res.ok || !json.success || !json.playdate?.id) {
          if (json.code === 'PARK_NOT_FOUND' && schedule.parkId && !strip) {
            // Stale/invalid park id — fall back to label-only propose once.
            setStripCanonicalParkId(true)
            const labelOnly = buildCirclePlaydateScheduleFromDraft(draft, {
              parkOptions,
              stripCanonicalParkId: true,
            })
            if (!labelOnly) {
              setDraft((prev) => ({ ...prev, parkId: null }))
              setStep('park')
              setSubmitState({
                kind: 'error',
                message: 'That park is no longer available. Choose another.',
                code: 'PARK_NOT_FOUND',
              })
              trackPlayV2Event('playdate_proposal_failed', {
                activeDogId: persistence.dogId,
                relationshipId: persistence.relationshipId,
                errorCategory: 'PARK_NOT_FOUND',
                source: 'builder',
              })
              return
            }
            const retry = await fetch('/api/play/playdates', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Idempotency-Key': `builder-${persistence.relationshipId}-${labelOnly.scheduledStartAt}-label`,
              },
              body: JSON.stringify({
                dogId: persistence.dogId,
                relationshipId: persistence.relationshipId,
                ...labelOnly,
              }),
            })
            const retryJson = (await retry.json().catch(() => ({}))) as {
              success?: boolean
              code?: string
              error?: string
              playdate?: { id: string }
            }
            if (retry.ok && retryJson.success && retryJson.playdate?.id) {
              setSubmitState({ kind: 'success', playdateId: retryJson.playdate.id })
              trackPlayV2Event('playdate_proposal_succeeded', {
                activeDogId: persistence.dogId,
                relationshipId: persistence.relationshipId,
                playdateId: retryJson.playdate.id,
                parkId: null,
                schedulingMode: draft.timeMode,
                source: 'builder',
                result: 'label_fallback',
              })
              persistence.onProposed?.(retryJson.playdate.id)
              setStep('success')
              return
            }
            setDraft((prev) => ({ ...prev, parkId: null }))
            setStep('park')
            setSubmitState({
              kind: 'error',
              message: 'That park is no longer available. Choose another.',
              code: 'PARK_NOT_FOUND',
            })
            trackPlayV2Event('playdate_proposal_failed', {
              activeDogId: persistence.dogId,
              relationshipId: persistence.relationshipId,
              errorCategory: 'PARK_NOT_FOUND',
              source: 'builder',
            })
            return
          }
          setSubmitState({
            kind: 'error',
            message: json.error ?? 'Could not send playdate invitation',
            code: json.code,
          })
          trackPlayV2Event('playdate_proposal_failed', {
            activeDogId: persistence.dogId,
            relationshipId: persistence.relationshipId,
            errorCategory: json.code ?? 'UNKNOWN',
            source: 'builder',
          })
          return
        }
        setSubmitState({ kind: 'success', playdateId: json.playdate.id })
        trackPlayV2Event('playdate_proposal_succeeded', {
          activeDogId: persistence.dogId,
          relationshipId: persistence.relationshipId,
          playdateId: json.playdate.id,
          parkId: schedule.parkId ?? null,
          schedulingMode: draft.timeMode,
          source: 'builder',
        })
        persistence.onProposed?.(json.playdate.id)
        setStep('success')
      } catch {
        setSubmitState({
          kind: 'error',
          message: 'Network error — try again',
          code: 'NETWORK',
        })
        trackPlayV2Event('playdate_proposal_failed', {
          activeDogId: persistence.dogId,
          relationshipId: persistence.relationshipId,
          errorCategory: 'NETWORK',
          source: 'builder',
        })
      }
    },
    [draft, persistence, parkOptions, stripCanonicalParkId]
  )

  const goNext = () => {
    const idx = WIZARD_STEPS.indexOf(step)
    if (idx < WIZARD_STEPS.length - 1) {
      setStep(WIZARD_STEPS[idx + 1]!)
      return
    }
    if (step === 'review') {
      if (persistEnabled) {
        void submitProposal()
        return
      }
      setStep('success')
    }
  }

  const park = resolvePlaydateParkOption(draft.parkId, parkOptions)
  const dateLabel = getPlaydateDateLabel(draft.dateId)
  const timeLabel =
    draft.timeMode === 'exact' && draft.exactTime
      ? (() => {
          const [h, m] = draft.exactTime.split(':').map(Number)
          const d = new Date()
          d.setHours(h ?? 0, m ?? 0, 0, 0)
          return d.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          })
        })()
      : getPlaydateTimeLabel(draft.timeWindow)
  const whenSummary = formatPlaydateBuilderWhenSummary({
    dateLabel,
    timeLabel,
    timeMode: draft.timeMode,
  })

  return (
    <WagamourV2Modal
      isOpen={isOpen}
      onClose={handleClose}
      presentation="sheet"
      portalContainer={portalContainer}
      ariaLabelledBy={titleId}
      header={
        <div className="space-y-2">
          {step !== 'dogs' && step !== 'success' ? (
            <button
              type="button"
              className={`inline-flex min-h-[44px] items-center gap-1 text-sm text-white/75 hover:text-white ${playV2FocusRing}`}
              onClick={goBack}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back
            </button>
          ) : null}
          <WagamourV2ModalEyebrow>Plan a playdate</WagamourV2ModalEyebrow>
          <h2 id={titleId} className="text-base font-bold leading-snug" style={v2ModalStyles.headline}>
            {stepTitle}
          </h2>
          {step !== 'success' ? (
            <div
              className="flex items-center justify-center gap-1.5"
              aria-label={`Step ${Math.max(0, WIZARD_STEPS.indexOf(step)) + 1} of ${WIZARD_STEPS.length}`}
              data-playdate-builder-progress="true"
            >
              {WIZARD_STEPS.map((s, i) => (
                <span
                  key={s}
                  className={cn(
                    'h-1.5 rounded-full transition-all',
                    i <= WIZARD_STEPS.indexOf(step) ? 'w-6 bg-amber-300/80' : 'w-3 bg-white/20'
                  )}
                  aria-hidden
                />
              ))}
            </div>
          ) : null}
        </div>
      }
      footer={
        step === 'success' ? (
          <WagamourV2Button type="button" fullWidth onClick={handleClose}>
            Back to relationship
          </WagamourV2Button>
        ) : (
          <div className="space-y-2">
            <WagamourV2Button
              type="button"
              fullWidth
              disabled={!canContinue}
              onClick={goNext}
              data-playdate-builder-continue="true"
            >
              {step === 'review'
                ? persistEnabled
                  ? submitState.kind === 'submitting'
                    ? 'Sending…'
                    : 'Send invitation'
                  : 'Preview invitation'
                : 'Continue'}
            </WagamourV2Button>
            {step === 'review' ? (
              <p className="text-center text-[11px] text-white/55">
                {persistEnabled
                  ? 'Your friend will review the invitation before it is confirmed.'
                  : 'Preview only — nothing will be sent.'}
              </p>
            ) : null}
            {submitState.kind === 'error' ? (
              <p className="text-center text-xs text-rose-200" role="alert" data-playdate-builder-error="true">
                {submitState.message}
                {submitState.code === 'OPEN_PLAYDATE_EXISTS'
                  ? ' An open playdate already exists for this friendship.'
                  : ''}{' '}
                <button
                  type="button"
                  className="underline"
                  onClick={() => void submitProposal()}
                >
                  Retry
                </button>
              </p>
            ) : null}
          </div>
        )
      }
      contentClassName="pb-2"
    >
      <div
        className="space-y-4"
        aria-labelledby={titleId}
        data-playdate-builder-preview-sheet="true"
        data-playdate-builder-step={step}
        data-playdate-builder-mode={persistEnabled ? 'persist' : 'preview'}
        data-playdate-builder-park-id={draft.parkId ?? undefined}
        data-playdate-builder-park-source={park?.source}
      >
        {step === 'dogs' ? (
          <div className="flex flex-wrap items-center justify-center gap-3">
            <div className="flex flex-col items-center gap-1">
              <PlayCircleOrbitAvatar
                name={activeName}
                photoUrl={activeDog.photoUrl}
                size={56}
                variant="center"
              />
              <span className="text-sm font-medium" style={v2ModalStyles.primary}>
                {activeName}
              </span>
            </div>
            <span className="text-white/40" aria-hidden>
              +
            </span>
            <div className="flex flex-col items-center gap-1">
              <PlayCircleOrbitAvatar
                name={friendName}
                photoUrl={seedFriend.photoUrl}
                size={56}
                variant="friend"
                selected
              />
              <span className="text-sm font-medium" style={v2ModalStyles.primary}>
                {friendName}
              </span>
            </div>
          </div>
        ) : null}

        {step === 'park' ? (
          <div className="space-y-2" role="radiogroup" aria-label="Playdate location">
            {parkOptions.length === 0 ? (
              <p
                className="rounded-[18px] border border-white/12 bg-white/[0.05] p-3 text-sm"
                style={v2ModalStyles.metadata}
                data-playdate-park-empty={
                  nearbyParksLoading ? 'loading' : 'empty'
                }
              >
                {nearbyParksLoading
                  ? 'Finding parks near your selected location…'
                  : 'No nearby parks for your selected location yet. Update your location in Parks, then try again.'}
              </p>
            ) : null}
            {parkOptions.map((p) => {
              const selected = draft.parkId === p.id
              return (
                <button
                  key={p.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-playdate-park-option={p.id}
                  data-playdate-park-source={p.source}
                  onClick={() => {
                    setDraft((prev) => ({ ...prev, parkId: p.id }))
                    setStripCanonicalParkId(false)
                    trackPlayV2Event('playdate_park_selected', {
                      parkId: p.canonicalParkId ?? p.id,
                      source:
                        p.source === 'handoff' || p.source === 'nearby'
                          ? 'parks'
                          : 'builder',
                      relationshipId: persistence?.relationshipId ?? null,
                      activeDogId: persistence?.dogId ?? null,
                    })
                  }}
                  className={cn(
                    'flex w-full min-h-[44px] items-start gap-3 rounded-[18px] border p-2.5 text-left',
                    selected
                      ? 'border-amber-200/45 bg-amber-400/12'
                      : 'border-white/12 bg-white/[0.05] hover:border-amber-200/25'
                  )}
                >
                  <Trees className="mt-1 h-5 w-5 shrink-0 text-amber-100/85" aria-hidden />
                  <div>
                    <p className="text-sm font-semibold" style={v2ModalStyles.primary}>
                      {p.name}
                    </p>
                    <p className="text-xs" style={v2ModalStyles.metadata}>
                      {p.descriptor}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        ) : null}

        {step === 'date' ? (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {PLAYDATE_PRIMARY_DATE_FIXTURES.map((d) => (
                <WagamourV2ModalChipToggle
                  key={d.id}
                  label={d.label}
                  selected={draft.dateId === d.id}
                  onToggle={() => setDraft((prev) => ({ ...prev, dateId: d.id }))}
                />
              ))}
              <WagamourV2ModalChipToggle
                label="More days"
                selected={showExtendedDates}
                onToggle={() => setShowExtendedDates((v) => !v)}
              />
            </div>
            {showExtendedDates ? (
              <div className="flex flex-wrap gap-2 border-t border-white/10 pt-2">
                {PLAYDATE_EXTENDED_DATE_FIXTURES.map((d) => (
                  <WagamourV2ModalChipToggle
                    key={d.id}
                    label={d.label}
                    selected={draft.dateId === d.id}
                    onToggle={() => setDraft((prev) => ({ ...prev, dateId: d.id }))}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {step === 'time' ? (
          <div
            className="space-y-3"
            role="group"
            aria-label="When"
            data-playdate-time-step="true"
          >
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label="Morning, Afternoon, Evening, or Specific time"
              data-playdate-time-options="true"
            >
              {PLAYDATE_TIME_WINDOWS.map((window) => (
                <WagamourV2ModalChipToggle
                  key={window.id}
                  label={window.label}
                  selected={
                    draft.timeMode === 'daypart' && draft.timeWindow === window.id
                  }
                  onToggle={() => {
                    setDraft((prev) => ({
                      ...prev,
                      timeMode: 'daypart',
                      timeWindow: window.id as PlaydateTimeWindow,
                      exactTime: null,
                    }))
                    trackPlayV2Event('playdate_daypart_selected', {
                      schedulingMode: 'daypart',
                      relationshipId: persistence?.relationshipId ?? null,
                      activeDogId: persistence?.dogId ?? null,
                      source: 'builder',
                    })
                  }}
                />
              ))}
              <WagamourV2ModalChipToggle
                label="Specific time"
                selected={draft.timeMode === 'exact'}
                onToggle={() => {
                  setDraft((prev) => ({
                    ...prev,
                    timeMode: 'exact',
                    timeWindow: null,
                    exactTime: prev.exactTime ?? '14:30',
                  }))
                  trackPlayV2Event('playdate_exact_time_selected', {
                    schedulingMode: 'exact',
                    relationshipId: persistence?.relationshipId ?? null,
                    activeDogId: persistence?.dogId ?? null,
                    source: 'builder',
                  })
                }}
              />
            </div>
            {draft.timeMode === 'exact' ? (
              <label className="block space-y-1.5">
                <span className="text-xs" style={v2ModalStyles.metadata}>
                  Exact time
                </span>
                <input
                  type="time"
                  value={draft.exactTime ?? '14:30'}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      timeMode: 'exact',
                      timeWindow: null,
                      exactTime: e.target.value || null,
                    }))
                  }
                  className={cn(v2ModalInputClassName, playV2FocusRing)}
                  style={v2ModalSelectStyle()}
                  data-playdate-exact-time="true"
                />
              </label>
            ) : null}
          </div>
        ) : null}

        {step === 'review' ? (
          <div
            className="space-y-3.5 rounded-[20px] border px-4 py-4"
            style={v2ModalStyles.nestedCard}
            data-playdate-review-plan="true"
          >
            <WagamourV2ModalStoryLine label="Dogs">
              {activeName} + {friendName}
            </WagamourV2ModalStoryLine>
            <WagamourV2ModalStoryLine label="Where">
              {park?.name ?? 'Choose a park'}
            </WagamourV2ModalStoryLine>
            <WagamourV2ModalStoryLine label="When">
              {whenSummary || 'Choose a time'}
            </WagamourV2ModalStoryLine>
          </div>
        ) : null}

        {step === 'success' ? (
          <div className="space-y-3 text-center" role="status" data-playdate-builder-success="true">
            <p className="text-sm leading-relaxed" style={v2ModalStyles.body}>
              {persistEnabled
                ? `Invitation sent for ${activeName} and ${friendName} · ${[whenSummary, park?.name].filter(Boolean).join(' · ')}.`
                : `${activeName} and ${friendName} would receive a playdate invitation for ${[whenSummary, park?.name].filter(Boolean).join(' · ')}.`}
            </p>
            <p className="text-xs text-amber-100/90">
              {persistEnabled
                ? 'Waiting for their response — you will see it in Updates.'
                : 'Preview only — nothing was sent.'}
            </p>
          </div>
        ) : null}
      </div>
    </WagamourV2Modal>
  )
}
